<script setup>
import { computed, ref } from 'vue';
import { useAlert } from 'dashboard/composables';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import API from 'dashboard/api/talkviaEmailCampaigns';
import { emailCampaignCopy } from '../emailCampaignCopy';
import {
  campaignStatus,
  deliveryStatus,
  exclusionLabels,
  campaignError,
  dateLabel,
} from '../emailCampaigns';

const emit = defineEmits(['dispatched']);
const copy = emailCampaignCopy;
const dialog = ref(null);
const campaign = ref(null);
const preview = ref(null);
const busy = ref(false);
const confirmed = ref(false);
const page = ref(1);
const document = computed(
  () =>
    `<html lang="pt-BR"><head><meta name="viewport" content="width=device-width, initial-scale=1"><style>body{font:15px/1.5 Arial,sans-serif;margin:16px;overflow-wrap:anywhere;color:#222}img{max-width:100%;height:auto}table{max-width:100%}</style></head><body>${campaign.value?.html_body || ''}</body></html>`
);

const load = async () => {
  const { data } = await API.show(campaign.value.id, page.value);
  campaign.value = data;
};

const open = async selected => {
  campaign.value = selected;
  preview.value = null;
  confirmed.value = false;
  page.value = 1;
  busy.value = true;
  dialog.value.open();
  try {
    await load();
    if (campaign.value.status === 'draft') {
      const { data } = await API.preview(campaign.value.id);
      preview.value = data;
    }
  } catch (error) {
    useAlert(campaignError(error));
  } finally {
    busy.value = false;
  }
};

const refresh = async (offset = 0) => {
  if (busy.value) return;
  busy.value = true;
  const previous = page.value;
  page.value += offset;
  try {
    await load();
  } catch (error) {
    page.value = previous;
    useAlert(campaignError(error));
  } finally {
    busy.value = false;
  }
};

const dispatch = async () => {
  if (busy.value || !confirmed.value || !preview.value?.eligible) return;
  busy.value = true;
  try {
    await API.dispatch(campaign.value.id);
    await load();
    useAlert(
      campaign.value.status === 'scheduled'
        ? 'Campanha agendada.'
        : 'Campanha confirmada. O envio foi iniciado.'
    );
    emit('dispatched');
  } catch (error) {
    useAlert(campaignError(error));
  } finally {
    busy.value = false;
  }
};

const close = () => dialog.value?.close();
defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialog"
    :title="copy.reviewTitle"
    width="4xl"
    solid
    show-close-icon
    sticky-header-footer
    overflow-y-auto
    :show-confirm-button="false"
    :show-cancel-button="false"
    :close-on-click-outside="false"
  >
    <div v-if="campaign" class="flex min-w-0 flex-col gap-4 pb-4">
      <div class="flex flex-wrap items-start justify-between gap-2">
        <div class="min-w-0">
          <h3 class="m-0 break-words text-base font-semibold text-n-slate-12">
            {{ campaign.title }}
          </h3>
          <p class="mb-0 break-words text-sm text-n-slate-11">
            {{ campaign.inbox_name }}{{ copy.separator }}
            {{ campaignStatus[campaign.status] }}
          </p>
        </div>
        <Button
          v-if="campaign.status !== 'draft'"
          type="button"
          :label="copy.refreshResult"
          variant="faded"
          color="slate"
          :is-loading="busy"
          @click="refresh()"
        />
      </div>
      <p class="m-0 text-sm text-n-slate-12">
        <strong>{{ copy.subjectPrefix }}</strong> {{ campaign.subject }}
      </p>
      <p class="m-0 text-sm text-n-slate-11">
        {{ dateLabel(campaign.scheduled_at) }}{{ copy.separator }}
        {{ `${campaign.rate_per_minute} ${copy.rateUnit}` }}
      </p>
      <iframe
        :title="copy.previewTitle"
        sandbox=""
        referrerpolicy="no-referrer"
        :srcdoc="document"
        class="h-80 w-full rounded-lg border border-n-weak bg-white"
      />
      <p class="m-0 text-xs text-n-slate-11">{{ copy.previewHint }}</p>
      <template v-if="campaign.status === 'draft'">
        <p v-if="busy" class="m-0 text-sm text-n-slate-11">
          {{ copy.checkingAudience }}
        </p>
        <template v-else-if="preview">
          <p class="m-0 text-sm text-n-slate-12">
            {{
              `${preview.eligible} ${copy.eligible} ${preview.total} ${copy.contacts}`
            }}
          </p>
          <p
            v-for="(count, reason) in preview.excluded"
            :key="reason"
            class="m-0 text-sm text-n-slate-11"
          >
            {{ `${exclusionLabels[reason]}: ${count}` }}
          </p>
          <div class="flex items-start gap-2 text-sm text-n-slate-12">
            <input
              id="email-campaign-confirm"
              v-model="confirmed"
              type="checkbox"
              class="mt-1 mb-0"
            />
            <label for="email-campaign-confirm" class="mb-0">{{
              copy.confirmConsent
            }}</label>
          </div>
        </template>
      </template>
      <template v-else>
        <div class="flex flex-wrap gap-x-5 gap-y-2 text-sm text-n-slate-12">
          <span v-for="(count, status) in campaign.counts" :key="status">{{
            `${deliveryStatus[status]}: ${count}`
          }}</span>
        </div>
        <p class="m-0 text-xs text-n-slate-11">{{ copy.deliveryHint }}</p>
        <div class="overflow-x-auto rounded-lg border border-n-weak">
          <table class="mb-0 w-full text-left text-sm">
            <thead>
              <tr>
                <th>{{ copy.contact }}</th>
                <th>{{ copy.status }}</th>
                <th>{{ copy.details }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="delivery in campaign.deliveries" :key="delivery.id">
                <td class="break-all">
                  {{ delivery.contact_name }}<br />{{ delivery.email }}
                </td>
                <td>{{ deliveryStatus[delivery.status] }}</td>
                <td class="min-w-40">
                  {{
                    delivery.error_message ||
                    (delivery.sent_at ? dateLabel(delivery.sent_at) : '—')
                  }}
                </td>
              </tr>
              <tr v-if="!campaign.deliveries?.length">
                <td colspan="3">{{ copy.noDeliveries }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div
          v-if="campaign.delivery_total > 50"
          class="flex flex-wrap items-center justify-end gap-2"
        >
          <Button
            type="button"
            :label="copy.previous"
            variant="faded"
            color="slate"
            :disabled="page === 1 || busy"
            @click="refresh(-1)"
          />
          <span class="text-sm text-n-slate-11">{{
            `${copy.page} ${page}`
          }}</span>
          <Button
            type="button"
            :label="copy.next"
            variant="faded"
            color="slate"
            :disabled="page * 50 >= campaign.delivery_total || busy"
            @click="refresh(1)"
          />
        </div>
      </template>
    </div>
    <template #footer>
      <div class="flex flex-col-reverse justify-end gap-3 sm:flex-row">
        <Button
          type="button"
          :label="copy.close"
          variant="faded"
          color="slate"
          @click="dialog.close()"
        />
        <Button
          v-if="campaign?.status === 'draft'"
          type="button"
          :label="
            campaign.scheduled_at ? 'Confirmar agendamento' : 'Confirmar envio'
          "
          :disabled="!confirmed || !preview?.eligible"
          :is-loading="busy"
          @click="dispatch"
        />
      </div>
    </template>
  </Dialog>
</template>
